import { useMemo } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import { useAerodromes, type Aerodrome } from '../../api/aerodromes'

// The airfield field of the flight log and the flight school's log form: a
// lookup over the aerodromes (plan.waypoints of type 'ad') that filters as
// the member types -- by code or by name ("EFNU", "numm"). mik-ng's picker
// does the same, and what made it fast is kept here: the list is fetched
// once for the whole session (api/aerodromes.ts) and filtered in the
// browser, so a keystroke never waits on the network, and only the best
// matches are drawn.
//
// An airfield that is not in the list (a field abroad, a strip nobody has
// added) can still be typed: the value is a code, 2 to 10 letters or
// digits, and is what the flight log stores.

const MAX_SHOWN = 30
const NO_MATCH = 6
const CODE = /[^A-Z0-9]/g
export const AERODROME_CODE = /^[A-Z0-9]{2,10}$/

// A code that starts with what was typed first, then one that contains it,
// then a name that does; the list's own (alphabetical) order within each.
const rank = (option: Aerodrome, q: string) => {
  const code = option.code.toLowerCase()
  if (code === q) return 0
  if (code.startsWith(q)) return 1
  if (code.includes(q)) return 2
  const name = (option.name ?? '').toLowerCase()
  if (name.startsWith(q)) return 3
  if (name.split(/[\s-]+/).some((word) => word.startsWith(q))) return 4
  return name.includes(q) ? 5 : NO_MATCH
}

export function filterAerodromes(options: Aerodrome[], input: string): Aerodrome[] {
  const q = input.trim().toLowerCase()
  if (!q) return options.slice(0, MAX_SHOWN)
  // One pass into buckets by rank: the list's own order is kept within each,
  // and nothing is sorted.
  const buckets: Aerodrome[][] = [[], [], [], [], [], [], []]
  for (const option of options) buckets[rank(option, q)]?.push(option)
  return buckets.slice(0, NO_MATCH).flat().slice(0, MAX_SHOWN)
}

// The Autocomplete's own filter would re-rank nothing and only trim; ours
// does both, so it replaces it outright.
const filterOptions = (options: Aerodrome[], state: { inputValue: string }) => filterAerodromes(options, state.inputValue)

export const toCode = (text: string) => text.toUpperCase().replace(CODE, '').slice(0, 10)

export default function AerodromeField({
  label,
  value,
  onChange,
  error,
  helperText,
  disabled,
}: {
  label: string
  /** The airfield code, '' for none. */
  value: string
  onChange: (code: string) => void
  error?: boolean
  helperText?: string
  disabled?: boolean
}) {
  const { data: aerodromes = [] } = useAerodromes()
  const selected = useMemo(() => aerodromes.find((a) => a.code === value) ?? null, [aerodromes, value])

  return (
    <Autocomplete<Aerodrome, false, false, true>
      freeSolo
      autoHighlight
      autoSelect
      selectOnFocus
      handleHomeEndKeys
      disabled={disabled}
      options={aerodromes}
      filterOptions={filterOptions}
      value={selected ?? value}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.code)}
      isOptionEqualToValue={(a, b) => (typeof a === 'string' || typeof b === 'string' ? a === b : a.code === b.code)}
      onChange={(_, next) => onChange(next === null ? '' : typeof next === 'string' ? toCode(next) : next.code)}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props} sx={{ display: 'flex', gap: 1.5, minHeight: 44 }}>
          <b>{option.code}</b>
          <span>{option.name}</span>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="EFNU"
          error={error}
          helperText={helperText}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false },
          }}
        />
      )}
    />
  )
}
